import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormHelperText,
  Tooltip,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon,
  DataObject as DataObjectIcon,
  Build as BuildIcon,
  Draw as DrawIcon,
  CheckBox as CheckBoxIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import AuthLayout from '../components/AuthLayout';
import checklistTemplateService, { ChecklistTemplate, ChecklistItem } from '../services/checklistTemplate.service';
import { useAuth } from '../contexts/AuthContext';

// Tipos de item de checklist com ícones e descrições
const itemTypes = [
  { value: 'simple_check', label: 'Verificação Simples', icon: <CheckBoxIcon />, description: 'Item de verificação básica (sim/não)' },
  { value: 'image_capture', label: 'Captura de Imagem', icon: <PhotoCameraIcon />, description: 'Solicitar foto ou imagem' },
  { value: 'update_data', label: 'Atualizar Dados', icon: <DataObjectIcon />, description: 'Atualizar informações do sistema' },
  { value: 'update_equipment', label: 'Atualizar Equipamento', icon: <BuildIcon />, description: 'Registrar manutenção ou atualização de equipamento' },
  { value: 'collect_signature', label: 'Coletar Assinatura', icon: <DrawIcon />, description: 'Solicitar assinatura do cliente ou técnico' },
  { value: 'value_input', label: 'Entrada de Valor', icon: <DataObjectIcon />, description: 'Permitir a entrada de um valor específico (medição, leitura, etc.)' }
];

// Tipos de serviço
const serviceTypes = [
  { value: 'maintenance', label: 'Manutenção' },
  { value: 'installation', label: 'Instalação' },
  { value: 'inspection', label: 'Inspeção' },
  { value: 'repair', label: 'Reparo' },
  { value: 'other', label: 'Outro' }
];

// Item vazio para inicialização
const emptyItem: ChecklistItem = {
  description: '',
  executionOrder: 1,
  requireSignature: false,
  type: 'simple_check',
  value: '',
  parentItemId: null,
  isActive: true
};

const ChecklistTemplateForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isEditMode = id !== 'new' && id !== undefined;

  // Função para verificar se um item é descendente de outro
  const isDescendantOf = (childIndex: number, ancestorIndex: number): boolean => {
    const child = formData.items[childIndex];
    if (!child.parentItemId) return false;
    
    // Se o item pai é o ancestral em questão
    if (child.parentItemId === formData.items[ancestorIndex]._id || child.parentItemId === `temp-${ancestorIndex}`) {
      return true;
    }
    
    // Caso contrário, verificar recursivamente os pais
    const parentIndex = formData.items.findIndex(item => 
      item._id === child.parentItemId || `temp-${formData.items.indexOf(item)}` === child.parentItemId
    );
    
    if (parentIndex === -1) return false;
    return isDescendantOf(parentIndex, ancestorIndex);
  };
  
  // Verificar se definir parentIndex como pai de index criaria um ciclo
  const wouldCreateCycle = (index: number, parentIndex: number): boolean => {
    // Se o potencial pai é descendente do item atual, isso criaria um ciclo
    return isDescendantOf(parentIndex, index);
  };
  
  // Obter o nível de profundidade de um item na hierarquia
  const getItemLevel = (index: number): number => {
    const item = formData.items[index];
    if (!item.parentItemId) return 0;
    
    const parentIndex = formData.items.findIndex(i => 
      i._id === item.parentItemId || `temp-${formData.items.indexOf(i)}` === item.parentItemId
    );
    
    if (parentIndex === -1) return 0;
    return 1 + getItemLevel(parentIndex);
  };
  
  // Obter cor com base no nível de hierarquia
  const getHierarchyColor = (level: number): string => {
    // Array de cores para diferentes níveis
    const colors = [
      '#2196f3', // Azul - nível 1
      '#4caf50', // Verde - nível 2
      '#ff9800', // Laranja - nível 3
      '#9c27b0', // Roxo - nível 4
      '#e91e63', // Rosa - nível 5
      '#607d8b'  // Cinza azulado - nível 6+
    ];
    
    // Usar a cor correspondente ao nível ou a última cor para níveis mais profundos
    return colors[Math.min(level - 1, colors.length - 1)];
  };
  
  // Obter o nome do item pai
  const getParentItemName = (parentItemId: string | null | undefined): string => {
    if (!parentItemId) return '';
    
    // Se o ID começa com 'temp-', é um item novo que ainda não foi salvo
    if (parentItemId.startsWith('temp-')) {
      const index = parseInt(parentItemId.replace('temp-', ''));
      return formData.items[index]?.description || `Item #${index + 1}`;
    }
    
    // Caso contrário, procurar pelo ID real
    const parentItem = formData.items.find(item => item._id === parentItemId);
    return parentItem ? parentItem.description : '';
  };

  // Estados
  const [formData, setFormData] = useState<ChecklistTemplate>({
    name: '',
    description: '',
    serviceType: 'maintenance',
    items: [{ ...emptyItem }],
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [itemErrors, setItemErrors] = useState<Record<string, Record<string, string>>>({});

  // Carregar dados do template se estiver em modo de edição
  useEffect(() => {
    if (isEditMode) {
      loadTemplateData();
    }
  }, [id]);

  const loadTemplateData = async () => {
    try {
      const { checklistTemplate } = await checklistTemplateService.getChecklistTemplateById(id!);
      setFormData(checklistTemplate);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao carregar dados do template');
    } finally {
      setLoadingData(false);
    }
  };

  // Manipuladores de eventos
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro do campo quando o usuário digita
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleItemChange = (index: number, field: keyof ChecklistItem, value: any) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    setFormData(prev => ({ ...prev, items: updatedItems }));
    
    // Limpar erro do campo quando o usuário altera
    if (itemErrors[index]?.[field]) {
      setItemErrors(prev => ({
        ...prev,
        [index]: { ...prev[index], [field]: '' }
      }));
    }
  };

  const handleAddItem = () => {
    const newItem: ChecklistItem = {
      ...emptyItem,
      executionOrder: formData.items.length + 1
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };
  
  // Função para adicionar um subitem a um item existente
  const handleAddSubitem = (parentIndex: number) => {
    const parentItem = formData.items[parentIndex];
    const parentId = parentItem._id || `temp-${parentIndex}`;
    
    const newItem: ChecklistItem = {
      ...emptyItem,
      executionOrder: formData.items.length + 1,
      parentItemId: parentId
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const handleRemoveItem = (index: number) => {
    if (formData.items.length <= 1) {
      return; // Manter pelo menos um item
    }
    
    const itemToRemove = formData.items[index];
    const itemId = itemToRemove._id || `temp-${index}`;
    
    // Encontrar todos os descendentes do item a ser removido
    const isDescendantOfRemovedItem = (item: ChecklistItem, itemIndex: number): boolean => {
      if (!item.parentItemId) return false;
      
      // Se o item pai é o item removido
      if (item.parentItemId === itemId) return true;
      
      // Verificar recursivamente
      const parentIndex = formData.items.findIndex(i => 
        i._id === item.parentItemId || `temp-${formData.items.indexOf(i)}` === item.parentItemId
      );
      
      if (parentIndex === -1) return false;
      return isDescendantOfRemovedItem(formData.items[parentIndex], parentIndex);
    };
    
    // Filtrar o item removido e todos os seus descendentes
    const updatedItems = formData.items.filter((item, i) => {
      return i !== index && !isDescendantOfRemovedItem(item, i);
    });
    
    // Reordenar os itens restantes
    const reorderedItems = updatedItems.map((item, i) => ({
      ...item,
      executionOrder: i + 1
    }));
    
    setFormData(prev => ({
      ...prev,
      items: reorderedItems
    }));
    
    // Atualizar erros de itens
    const updatedItemErrors = { ...itemErrors };
    
    // Remover erros para o item removido e seus descendentes
    Object.keys(updatedItemErrors).forEach(key => {
      const keyIndex = parseInt(key);
      const item = formData.items[keyIndex];
      
      if (keyIndex === index || (item && isDescendantOfRemovedItem(item, keyIndex))) {
        delete updatedItemErrors[keyIndex];
      }
    });
    
    // Reindexar os erros
    const newItemErrors: Record<string, Record<string, string>> = {};
    let newIndex = 0;
    
    formData.items.forEach((item, oldIndex) => {
      if (oldIndex !== index && !isDescendantOfRemovedItem(item, oldIndex)) {
        if (updatedItemErrors[oldIndex]) {
          newItemErrors[newIndex] = updatedItemErrors[oldIndex];
        }
        newIndex++;
      }
    });
    
    setItemErrors(newItemErrors);
  };

  // Função para reordenar itens com drag and drop
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    
    const items = Array.from(formData.items);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Atualizar a ordem de execução
    const updatedItems = items.map((item, index) => ({
      ...item,
      executionOrder: index + 1
    }));
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  // Validação do formulário
  const validateForm = () => {
    const errors: Record<string, string> = {};
    const itemValidationErrors: Record<string, Record<string, string>> = {};
    let isValid = true;
    
    // Validar campos do formulário
    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
      isValid = false;
    }
    
    if (!formData.serviceType) {
      errors.serviceType = 'Tipo de serviço é obrigatório';
      isValid = false;
    }
    
    // Validar itens do checklist
    formData.items.forEach((item, index) => {
      const itemErrors: Record<string, string> = {};
      
      if (!item.description.trim()) {
        itemErrors.description = 'Descrição é obrigatória';
        isValid = false;
      }
      
      if (!item.type) {
        itemErrors.type = 'Tipo é obrigatório';
        isValid = false;
      }
      
      if (Object.keys(itemErrors).length > 0) {
        itemValidationErrors[index] = itemErrors;
      }
    });
    
    setFormErrors(errors);
    setItemErrors(itemValidationErrors);
    
    return isValid;
  };

  // Enviar formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Por favor, corrija os erros no formulário');
      return;
    }
    
    setLoading(true);
    
    try {
      if (isEditMode) {
        // Garantir que temos um ID válido para edição
        if (!id) {
          setError('ID do template não encontrado');
          return;
        }
        await checklistTemplateService.updateChecklistTemplate(id, formData);
      } else {
        // Criar novo template
        await checklistTemplateService.createChecklistTemplate(formData);
      }
      
      navigate('/checklist-templates');
    } catch (error: any) {
      console.error('Erro ao salvar template:', error);
      setError(error.response?.data?.message || 'Erro ao salvar template de checklist');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <AuthLayout title="Carregando Template...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Template de Checklist">
      <Box sx={{ p: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
            Início
          </Link>
          <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/checklist-templates'); }}>
            Templates de Checklist
          </Link>
          <Typography color="text.primary">
            {isEditMode ? 'Editar Template' : 'Novo Template'}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {isEditMode ? 'Editar Template de Checklist' : 'Novo Template de Checklist'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/checklist-templates')}
          >
            Voltar
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Informações Básicas
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome do Template *"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de Serviço *"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleInputChange}
                  error={!!formErrors.serviceType}
                  helperText={formErrors.serviceType}
                  required
                >
                  {serviceTypes.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isActive}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                  }
                  label="Ativo"
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Itens do Checklist
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
              >
                Adicionar Item
              </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Arraste os itens para reordenar. A ordem de execução será atualizada automaticamente.
            </Typography>
            
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="checklist-items">
                {(provided) => (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {formData.items.map((item, index) => (
                      <Draggable key={index} draggableId={`item-${index}`} index={index}>
                        {(provided) => (
                          <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{ 
                              mb: 2, 
                              position: 'relative',
                              ml: item.parentItemId ? (getItemLevel(index) * 20) : 0, // Recuo proporcional ao nível
                              borderLeft: item.parentItemId ? `4px solid ${getHierarchyColor(getItemLevel(index))}` : 'none' // Cor diferente para cada nível
                            }}
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box {...provided.dragHandleProps} sx={{ mr: 1, cursor: 'grab' }}>
                                  <DragIndicatorIcon />
                                </Box>
                                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                                  {item.parentItemId && (
                                    <Box 
                                      component="span" 
                                      sx={{ 
                                        mr: 1, 
                                        color: getHierarchyColor(getItemLevel(index)),
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        border: `1px solid ${getHierarchyColor(getItemLevel(index))}`,
                                        borderRadius: '4px',
                                        padding: '0px 4px'
                                      }}
                                    >
                                      NÍVEL {getItemLevel(index)}
                                    </Box>
                                  )}
                                  Item #{item.executionOrder}
                                </Typography>
                                {item.parentItemId && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2, fontStyle: 'italic' }}>
                                    Item pai: {getParentItemName(item.parentItemId)}
                                  </Typography>
                                )}
                                <Box sx={{ flexGrow: 1 }} />
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveItem(index)}
                                  disabled={formData.items.length <= 1}
                                  title="Remover Item"
                                >
                                  <DeleteIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleAddSubitem(index)}
                                  color="primary"
                                  sx={{ ml: 1 }}
                                  title="Adicionar Subitem"
                                >
                                  <AddIcon />
                                </IconButton>
                              </Box>
                              
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label="Descrição *"
                                    value={item.description}
                                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                    error={!!itemErrors[index]?.description}
                                    helperText={itemErrors[index]?.description}
                                    required
                                  />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    select
                                    label="Tipo de Item *"
                                    value={item.type}
                                    onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                                    error={!!itemErrors[index]?.type}
                                    helperText={itemErrors[index]?.type}
                                    required
                                  >
                                    {itemTypes.map(option => (
                                      <MenuItem key={option.value} value={option.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          {option.icon}
                                          <Box sx={{ ml: 1 }}>{option.label}</Box>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <FormHelperText>
                                    {itemTypes.find(t => t.value === item.type)?.description}
                                  </FormHelperText>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    select
                                    label="Item Pai"
                                    value={item.parentItemId || ''}
                                    onChange={(e) => handleItemChange(index, 'parentItemId', e.target.value || null)}
                                    helperText="Selecione um item pai (opcional)"
                                  >
                                    <MenuItem value="">Nenhum (item principal)</MenuItem>
                                    {formData.items.map((parentItem, parentIndex) => {
                                      // Não permitir selecionar o próprio item como pai
                                      // Não permitir selecionar itens que criariam ciclos na hierarquia
                                      if (parentIndex !== index && !wouldCreateCycle(index, parentIndex)) {
                                        // Calcular o nível de indentação para mostrar visualmente a hierarquia no dropdown
                                        const level = getItemLevel(parentIndex);
                                        const indentation = level > 0 ? '─'.repeat(level) + '▸ ' : '';
                                        
                                        return (
                                          <MenuItem 
                                            key={parentItem._id || `new-${parentIndex}`} 
                                            value={parentItem._id || `temp-${parentIndex}`}
                                          >
                                            {indentation}{parentItem.description || `Item #${parentItem.executionOrder}`}
                                          </MenuItem>
                                        );
                                      }
                                      return null;
                                    })}
                                  </TextField>
                                </Grid>
                                
                                {/* Campo de valor financeiro para todos os tipos de itens */}
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label="Valor Financeiro (R$)"
                                    value={item.value || ''}
                                    onChange={(e) => handleItemChange(index, 'value', e.target.value)}
                                    helperText="Valor financeiro associado a este item"
                                    type="number"
                                    InputProps={{
                                      startAdornment: <Box component="span" sx={{ mr: 1 }}>R$</Box>
                                    }}
                                  />
                                </Grid>
                                
                                <Grid item xs={12}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={item.requireSignature}
                                        onChange={(e) => handleItemChange(index, 'requireSignature', e.target.checked)}
                                      />
                                    }
                                    label="Exigir assinatura para este item"
                                  />
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
            
            {formData.items.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Nenhum item adicionado
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  sx={{ mt: 2 }}
                >
                  Adicionar Item
                </Button>
              </Box>
            )}
          </Paper>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/checklist-templates')}
              sx={{ mr: 2 }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : isEditMode ? 'Atualizar' : 'Salvar'}
            </Button>
          </Box>
        </form>
      </Box>
    </AuthLayout>
  );
};

export default ChecklistTemplateForm;
